import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
// import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";
import ReactRouterLink from "./ReactRouterLink";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { TextField } from "./InputFields/TextField";
import { FormActionButton } from "./FormActionButton";
import { PasswordInputField } from "./InputFields/PasswordInputField";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import {
  createUser,
  fetchUserByEmail,
  fetchUserByEmailAndPassword,
  resetUserState,
  updateUserPassword,
} from "../../redux/actions/userActions";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import bcrypt from "bcryptjs";
import { ErrorMessage } from "./ErrorMessage";
import {
  currentTimestamp,
  generateHashPassword,
  getDifferenceInMinutesBetweenTimeStamps,
  getLoginCookieAndSession,
  setCookie,
  setSession,
} from "../../utils";
import { USER_JUST_LOGGED_IN, USER_LOGIN_COOKIE } from "../../constants";
import { EmailInputField } from "./InputFields/EmailInputField";
import MuiAlert from "./MuiAlert";
import ReCAPTCHA from "react-google-recaptcha";
import { sendEmail } from "../../emailHandler";

interface FormProps {
  onToggle: () => void;
  openFormFrom?: "User" | "Seller";
  alertMessage?: string;
  isOpenFromOpenOtherPage?: boolean;
  onLogin?: () => void;
}

interface ISignInSignUpProps {
  openFormFrom: "User" | "Seller";
  alertMessage?: string;
  isOpenFromOpenOtherPage?: boolean;
  onLogin?: () => void;
}

function Copyright(props: any) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      align="center"
      sx={{ fontSize: "0.8rem" }}
      {...props}
    >
      {"By signing up or signing in, you agree to our "}
      <ReactRouterLink to="/blog/privacy" linkElement="Privacy Policy" />
      {" and "}
      <ReactRouterLink to="/blog/terms" linkElement="Terms of Service" />
      {". This site is protected by reCAPTCHA and the Google "}
      <ReactRouterLink
        to="https://policies.google.com/privacy"
        target="_blank"
        rel="noopener noreferrer"
        linkElement=" Privacy Policy"
      />
      {" and "}
      <ReactRouterLink
        to="https://policies.google.com/terms"
        target="_blank"
        rel="noopener noreferrer"
        linkElement="Terms of Service"
      />
      {" apply."}
    </Typography>
  );
}

const SignInForm: React.FC<FormProps> = ({
  openFormFrom,
  isOpenFromOpenOtherPage,
  onLogin,
}) => {
  const [isFormSubmitted, setIsFormSubmitted] = React.useState<boolean>(false);
  const [isAccountBlocked, setIsAccountBlocked] =
    React.useState<boolean>(false);
  const [loginBlockedTimeRemaining, setLoginBlockedTimeRemaining] =
    React.useState<number>(0);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading, users, error } = useSelector(
    (state: RootState) => state.users
  );

  const formRef = React.useRef<HTMLFormElement>(null);

  const formMethods = useForm<{
    email: string;
    password: string;
  }>({
    mode: "onBlur",
  });

  const { handleSubmit, control, setValue, getValues } = formMethods;

  React.useEffect(() => {
    if (users && users.length && isFormSubmitted) {
      if (users[0].loginAttempts < 4) {
        setCookie(USER_LOGIN_COOKIE, String(users[0].createdTime));
        setSession(USER_JUST_LOGGED_IN, String(users[0].createdTime));
        if (openFormFrom === "User") {
          if (isOpenFromOpenOtherPage) {
            onLogin && onLogin();
          } else {
            navigate(`/account`);
          }
        } else {
          navigate(`/presell`);
        }
      } else {
        if (users[0].lastWrongPasswordTime) {
          const remainingMinutes = getDifferenceInMinutesBetweenTimeStamps(
            currentTimestamp(),
            users[0].lastWrongPasswordTime
          );
          setLoginBlockedTimeRemaining(30 - remainingMinutes);
          setIsAccountBlocked(true);
        }
      }
    }
  }, [users]);

  const onSubmit: SubmitHandler<{
    email: string;
    password: string;
  }> = async (formData) => {
    // if (recaptchaRef.current) {
    if (true) {
      //const token = await recaptchaRef.current.executeAsync();
      // if (token) {
      if (true) {
        dispatch(
          fetchUserByEmailAndPassword(
            formData.email,
            formData.password,
            "",
            true
          )
        );
        setIsFormSubmitted(true);
      } else {
        console.error("Failed to get reCAPTCHA token.");
      }
    }
  };

  const onCloseAlert = () => {
    setIsAccountBlocked(false);
  };

  return (
    <>
      {isAccountBlocked ? (
        <MuiAlert
          text={`Your account has been temporarily locked due to multiple unsuccessful login attempts. Please try again after ${loginBlockedTimeRemaining} minutes or reset your password if you’ve forgotten it.`}
          type="info"
          shouldUseCookie={false}
          sx={{ borderRadius: "12px" }}
          onClose={onCloseAlert}
        />
      ) : null}
      <FormProvider {...formMethods}>
        <form ref={formRef} noValidate onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={loading} style={{ border: "none", padding: 0 }}>
            <Box sx={{ mt: 2, mb: 1 }}>
              <EmailInputField
                fieldName="email"
                required
                fieldLabel="Email Address"
              />
              <PasswordInputField
                fieldName="password"
                required
                fieldLabel="Password"
                sx={{ mb: "12px" }}
              />
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ""}
                size="invisible"
              />
              <FormActionButton
                text={loading ? <CircularProgress size={20} /> : "Sign In"}
              />
              {!loading && users && users.length === 0 && isFormSubmitted && (
                <ErrorMessage text="Incorrect Email or Password" />
              )}
            </Box>
          </fieldset>
        </form>
      </FormProvider>
    </>
  );
};

const SignUpForm: React.FC<FormProps> = ({
  onToggle,
  openFormFrom,
  isOpenFromOpenOtherPage,
  onLogin,
}) => {
  const [isFormSubmitted, setIsFormSubmitted] = React.useState<boolean>(false);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading, users, error } = useSelector(
    (state: RootState) => state.users
  );

  const formRef = React.useRef<HTMLFormElement>(null);
  const formMethods = useForm<{ email: string; password: string }>({
    mode: "onBlur",
  });

  const { handleSubmit, watch } = formMethods;

  React.useEffect(() => {
    if (users && users.length && isFormSubmitted) {
      setCookie(USER_LOGIN_COOKIE, String(users[0].createdTime));
      setSession(USER_JUST_LOGGED_IN, String(users[0].createdTime));
      if (openFormFrom === "User") {
        if (isOpenFromOpenOtherPage) {
          onLogin && onLogin();
        } else {
          navigate(`/account`);
        }
      } else {
        navigate(`/presell`);
      }
    }
  }, [users]);

  const onSubmit: SubmitHandler<{ email: string; password: string }> = async (
    formData
  ) => {
    //if (recaptchaRef.current) {
    //const token = await recaptchaRef.current.executeAsync();
    if (true) {
      dispatch(createUser(formData.email, formData.password));
      setIsFormSubmitted(true);
    } else {
      console.error("Failed to get reCAPTCHA token.");
    }
    // } else {
    //   console.error("Failed to get reCAPTCHA token.");
    // }
  };

  return (
    <FormProvider {...formMethods}>
      <form ref={formRef} noValidate onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={loading} style={{ border: "none", padding: 0 }}>
          <Box sx={{ mt: 2, mb: 1 }}>
            <EmailInputField
              fieldName="email"
              required
              fieldLabel="Email Address"
            />
            <PasswordInputField
              fieldName="password"
              required
              fieldLabel="Password"
              sx={{ mb: "20px" }}
            />
            <PasswordInputField
              fieldName="confirmPassword"
              fieldLabel="Confirm Password"
              required
              sx={{ mb: "12px" }}
              rules={{
                validate: (value) => {
                  if (value !== watch("password")) {
                    return "Passwords do not match";
                  }
                  return true;
                },
              }}
            />
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ""}
              size="invisible"
            />
            <FormActionButton
              text={loading ? <CircularProgress size={20} /> : "Sign Up"}
            />
            {!loading && users === null && isFormSubmitted && (
              <ErrorMessage text="User Already Exists" />
            )}
            <Grid container justifyContent="flex-end" sx={{ mt: 1, mb: 0 }}>
              <Grid item>
                <Link
                  variant="body2"
                  className="clickableLink"
                  onClick={onToggle}
                >
                  Already have an account? Sign in
                </Link>
              </Grid>
            </Grid>
          </Box>
        </fieldset>
      </form>
    </FormProvider>
  );
};

const ForgotPasswordForm: React.FC<FormProps> = ({ onToggle }) => {
  const [isFormSubmitted, setIsFormSubmitted] = React.useState<boolean>(false);
  const [afterEmailMessage, setAfterEmailMessage] = React.useState<string>("");

  const formRef = React.useRef<HTMLFormElement>(null);
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const formMethods = useForm<{ email: string; password: string }>({
    mode: "onBlur",
  });

  const dispatch = useDispatch();

  const { loading, users, error } = useSelector(
    (state: RootState) => state.users
  );

  const { handleSubmit } = formMethods;

  React.useEffect(() => {
    setAfterEmailMessage("");
    if (!error) {
      if (users && users.length && isFormSubmitted && users[0].password) {
        setAfterEmailMessage(
          "We've sent you a new password to your email inbox. Please check your spam or junk folder if you don't see it right away."
        );
      }
    } else {
      setAfterEmailMessage(error);
    }
  }, [users, error]);

  const onSubmit: SubmitHandler<{ email: string }> = async (formData) => {
    // if (recaptchaRef.current) {
    //   const token = await recaptchaRef.current.executeAsync();
    //   if (token) {
    //   } else {
    //     console.error("Failed to get reCAPTCHA token.");
    //   }
    // } else {
    //   console.error("Failed to get reCAPTCHA token.");
    // }
    dispatch(resetUserState());
    dispatch(updateUserPassword(formData.email));
    setIsFormSubmitted(true);
  };

  return (
    <>
      {afterEmailMessage && (
        <MuiAlert
          text={afterEmailMessage}
          type="info"
          shouldUseCookie={false}
          sx={{ borderRadius: "12px" }} // Add borderRadius styling here
        />
      )}
      <FormProvider {...formMethods}>
        <form
          ref={formRef}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          style={{ width: "100%" }}
        >
          <fieldset disabled={false} style={{ border: "none", padding: 0 }}>
            <Box sx={{ mt: 2, mb: 1 }}>
              <EmailInputField
                fieldName="email"
                required
                sx={{ mb: "12px" }}
                fieldLabel="Email Address"
              />
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ""}
                size="invisible"
              />

              <FormActionButton
                text={
                  loading ? <CircularProgress size={20} /> : "Reset Password"
                }
                onClick={() => {}}
              />
              {!loading && users && users.length === 0 && isFormSubmitted && (
                <ErrorMessage text="Incorrect Email" />
              )}
              <Grid container justifyContent="flex-end" sx={{ mt: 1, mb: 0 }}>
                <Grid item>
                  <Link
                    variant="body2"
                    className="clickableLink"
                    onClick={onToggle}
                  >
                    Remember your password? Sign in
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </fieldset>
        </form>
      </FormProvider>
    </>
  );
};

export default function SignInSignUp(props: ISignInSignUpProps) {
  const [showSignUp, setShowSignUp] = React.useState(true);
  const [showForgotPassword, setShowForgotPassword] = React.useState(false);
  const profileId = getLoginCookieAndSession();

  const handleToggleForm = (formType: string) => {
    setShowSignUp(formType === "signUp");
    setShowForgotPassword(formType === "forgotPassword");
  };

  return (
    <>
      {!profileId && (
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              spacing={1}
            >
              <Grid item>
                <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
                  <LockOutlinedIcon />
                </Avatar>
              </Grid>
              <Grid item>
                <Typography component="h2" variant="h5">
                  {showSignUp
                    ? "Sign up"
                    : showForgotPassword
                    ? "Forgot Password"
                    : "Sign in"}
                </Typography>
              </Grid>
            </Grid>
            {props.alertMessage ? (
              <MuiAlert
                text={props.alertMessage}
                type="info"
                shouldUseCookie={false}
                sx={{ borderRadius: "12px" }} // Add borderRadius styling here
              />
            ) : null}
            {showSignUp && (
              <SignUpForm
                onToggle={() => handleToggleForm("signIn")}
                openFormFrom={props.openFormFrom}
                isOpenFromOpenOtherPage={props.isOpenFromOpenOtherPage}
                onLogin={props.onLogin}
              />
            )}
            {showForgotPassword && (
              <ForgotPasswordForm onToggle={() => handleToggleForm("signIn")} />
            )}
            {!showSignUp && !showForgotPassword && (
              <SignInForm
                onToggle={() => handleToggleForm("signUp")}
                openFormFrom={props.openFormFrom}
                isOpenFromOpenOtherPage={props.isOpenFromOpenOtherPage}
                onLogin={props.onLogin}
              />
            )}

            {!showSignUp && !showForgotPassword && (
              <Grid container sx={{ mt: 0, mb: 1 }}>
                <Grid item xs>
                  <Link
                    variant="body2"
                    className="clickableLink"
                    onClick={() => handleToggleForm("forgotPassword")}
                  >
                    Forgot password?
                  </Link>
                </Grid>
                <Grid item>
                  <Link
                    variant="body2"
                    className="clickableLink"
                    onClick={() => handleToggleForm("signUp")}
                  >
                    {"No account? Sign up"}
                  </Link>
                </Grid>
              </Grid>
            )}
          </Box>
          <Box sx={{ mb: 1 }}>
            <Copyright />
          </Box>
        </Container>
      )}
    </>
  );
}
